import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import ProductCard from "@/components/GameCard";
import { LocalizedLink } from "@/i18n/LocalizedLink";
import { Key, Shield, Zap, Clock, ArrowRight, ShoppingBag, Sparkles, Lock } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Home() {
  const { t } = useLang();
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });
  const featured = products.slice(0, 8);
  const latest = [...products].slice(0, 6);

  return (
    <main data-testid="home-page">
      <section className="relative overflow-hidden">
        <div className="aurora" />
        <div className="grid-bg absolute inset-0 opacity-50" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 md:pb-32 relative">
          <motion.div {...fadeUp} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/5 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-brand-cyan" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan">{t("hero.eyebrow")}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6">
              {t("hero.title")}
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed max-w-2xl mb-10">{t("hero.subtitle")}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <LocalizedLink to="/verify" className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-7 py-4 rounded-lg transition-all glow-blue text-base" data-testid="hero-cta-primary">
                <Key className="w-4 h-4" />
                {t("hero.cta_primary")}
              </LocalizedLink>
              <LocalizedLink to="/games" className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold px-7 py-4 rounded-lg transition-all text-base" data-testid="hero-cta-secondary">
                <ShoppingBag className="w-4 h-4" />
                {t("hero.cta_secondary")}
              </LocalizedLink>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.8 }} className="hidden lg:block absolute end-12 top-32 w-72 float">
            <div className="bg-[#0c0c0c]/80 backdrop-blur-xl border border-white/10 rounded-xl p-5 pulse-glow">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-cyan font-bold mb-2">Steam Guard</div>
              <div className="font-mono text-3xl tracking-[0.5em] text-white">A7K9P</div>
              <div className="mt-3 h-1 bg-white/5 rounded overflow-hidden">
                <div className="h-full w-2/3 bg-gradient-to-r from-brand to-brand-cyan" />
              </div>
              <div className="text-xs text-zinc-500 mt-2">Fetched automatically in seconds</div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="how-it-works">
        <motion.div {...fadeUp} className="mb-12">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-3">{t("sections.how_it_works")}</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold">{t("sections.how_sub")}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ShoppingBag, num: "01", t: t("sections.step1_title"), d: t("sections.step1_desc") },
            { icon: Key, num: "02", t: t("sections.step2_title"), d: t("sections.step2_desc") },
            { icon: Zap, num: "03", t: t("sections.step3_title"), d: t("sections.step3_desc") },
          ].map((s, i) => (
            <motion.div key={i} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6" data-testid={`step-${i + 1}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="w-12 h-12 rounded-lg bg-brand/10 border border-brand/20 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-brand-cyan" />
                </div>
                <span className="font-mono text-3xl text-zinc-800 font-bold">{s.num}</span>
              </div>
              <h3 className="font-heading text-xl font-semibold mb-2">{s.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="popular-games">
          <motion.div {...fadeUp} className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">{t("sections.popular")}</div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold">{t("sections.popular_sub")}</h2>
            </div>
            <LocalizedLink to="/games" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1">
              {t("nav.games")} <ArrowRight className="w-4 h-4 rtl-flip" />
            </LocalizedLink>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="features">
        <motion.div {...fadeUp} className="mb-12">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-3">{t("sections.features")}</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold">KHA GAMES</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Lock, t: t("sections.f_secure"), d: t("sections.f_secure_desc") },
            { icon: Zap, t: t("sections.f_instant"), d: t("sections.f_instant_desc") },
            { icon: Shield, t: t("sections.f_steamguard"), d: t("sections.f_steamguard_desc") },
            { icon: Clock, t: t("sections.f_24_7"), d: t("sections.f_24_7_desc") },
          ].map((f, i) => (
            <motion.div key={i} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.08 }} className="bg-gradient-to-b from-[#0c0c0c] to-[#070707] border border-white/5 rounded-xl p-6 hover:border-brand/30 transition-colors">
              <div className="w-10 h-10 rounded-md bg-brand/10 border border-brand/20 flex items-center justify-center mb-4">
                <f.icon className="w-4 h-4 text-brand-cyan" />
              </div>
              <h3 className="font-heading font-semibold mb-2">{f.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
}
